import { useMemo, useState } from 'react'
import Seo, { softwareAppSchema } from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import Faq from '../components/Faq'
import type { QA } from '../components/Faq'
import ResultSheet from '../components/ResultSheet'
import CalcActions from '../components/CalcActions'
import InfoTip from '../components/InfoTip'
import { Field, SmartField, Segmented } from '../components/Inputs'
import { computeFlooring, DEFAULT_FLOORING } from '../calculators/flooring'
import type { FlooringInputs, FloorArea, FlooringType } from '../calculators/flooring'
import { calcsInCategory } from '../data/calculators'
import { saveSheet, genId } from '../lib/storage'
import { ROOM_PRESETS, CUSTOM_DIMS, CUSTOM_ROOM_KEY, toUnit, matchedPreset, dropdownValue, presetLabel } from '../lib/roomPresets'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'
import { calcName, categoryName } from '../i18n/calculators'

const PATH = '/calculators/flooring/'
const CATEGORY = 'flooring-and-tile'

interface CalcContent {
  title: string
  desc: string
  h1: string
  intro: string
  flooringTypeLabel: string
  typeLaminate: string
  typeVinyl: string
  typeHardwood: string
  boardWidthLabel: string
  boardWidthTip: string
  inSuffix: string
  transitionLabel: string
  transitionTip: string
  transitionSuffix: string
  glueDown: string
  glueDownTip: string
  wasteTip: string
  primaryName: string
  primaryLabel: string
  secondaryName: string
  secondaryLabel: (widthIn: number) => string
  savePrefix: string
  saveSummary: (boxes: number, type: FlooringType, rolls: number) => string
  areaSummary: (total: number, withWaste: number, boxes: number) => string
  assumptions: (total: number, rooms: number, wastePct: number) => string
  aboutH2: string
  aboutP1: string
  howH2: string
  howP1: string
  formula: string
  howP2: string
  mistakesH3: string
  mistakes: { pre?: string; strong: string; rest: string }[]
  relatedH2: string
  reviewer: string
  faqs: QA[]
}

const EN: CalcContent = {
  title: 'Flooring Calculator — How Many Boxes Do I Need? | RenoSheets',
  desc: 'Free flooring calculator for laminate, vinyl plank and hardwood. Enter room sizes to get box count plus underlayment, spacers and transition strips. No signup.',
  h1: 'Flooring Calculator',
  intro: 'Enter your room sizes to get box count for laminate, vinyl plank or hardwood — plus underlayment, spacers and transitions.',
  flooringTypeLabel: 'Flooring type',
  typeLaminate: 'Laminate',
  typeVinyl: 'Vinyl Plank',
  typeHardwood: 'Hardwood',
  boardWidthLabel: 'Board / plank width',
  boardWidthTip: 'Common widths: 3 in (strip hardwood), 5 in (standard hardwood/laminate), 7 in (wide plank). Check the product spec for exact coverage.',
  inSuffix: 'in',
  transitionLabel: 'Transition strips needed',
  transitionTip: 'Number of doorways or flooring transitions. 1 strip per opening where the floor meets a different surface.',
  transitionSuffix: 'strips',
  glueDown: 'Glue-down install (adds adhesive)',
  glueDownTip: 'Check if your vinyl plank or engineered hardwood is installed with adhesive rather than as a floating floor. Most LVP and laminate are floating.',
  wasteTip: '10% for straight layout; 12% for most rooms (default); 15–20% for diagonal layout or rooms with many angles.',
  primaryName: 'Boxes',
  primaryLabel: 'of flooring (20 sq ft / box)',
  secondaryName: 'Planks',
  secondaryLabel: (widthIn) => `${widthIn} in wide planks`,
  savePrefix: 'Flooring',
  saveSummary: (boxes, type, rolls) => `${boxes} boxes (${type}) · ${rolls} rolls underlayment`,
  areaSummary: (total, withWaste, boxes) => `${total} sq ft net → ${withWaste} sq ft with waste → ${boxes} boxes (20 sq ft/box)`,
  assumptions: (total, rooms, wastePct) => `${total} sq ft across ${rooms} room${rooms === 1 ? '' : 's'}, ${wastePct}% waste, 20 sq ft per box.`,
  aboutH2: 'What actually affects how much flooring you need',
  aboutP1: 'Waste percentage should track your layout pattern, not just room shape. A straight, parallel installation running the length of a rectangular room is the most efficient — most manufacturers\' 10% guidance assumes exactly this. A diagonal layout, herringbone pattern, or a room with lots of jogs, closets, and doorway transitions pushes real-world waste closer to 15–20%, since more cuts end up too short to reuse. Plank width matters for a different reason: narrower planks need more of them and create more seams, while wide-plank flooring covers area faster but shows gaps more visibly if the subfloor isn\'t flat. Always buy from the same dye lot and keep the extra box — manufacturers don\'t guarantee color matching between production runs, so a future repair with a different lot can stand out.',
  howH2: 'How to calculate flooring boxes',
  howP1: 'Flooring is sold by the box (typically 20 sq ft for laminate and vinyl plank). The formula is simple:',
  formula: `total area (sq ft) = sum of all room lengths × widths
area with waste = total area × (1 + waste%)
boxes = ceil(area with waste ÷ 20)`,
  howP2: 'For hardwood installed by the plank, divide the area by board width times average board length. Always buy one extra box from the same dye lot for repairs — colour varies between lots.',
  mistakesH3: 'Common mistakes',
  mistakes: [
    { pre: 'Not ', strong: 'leaving expansion gaps', rest: ' at walls and fixed objects — the floor will buckle in warm weather.' },
    { pre: 'Skipping ', strong: 'underlayment', rest: ' on concrete — moisture vapor will damage the planks over time.' },
    { pre: 'Installing over ', strong: 'uneven subfloor', rest: ' — floating floors amplify any high spots or dips, causing squeaks and click joints to separate.' },
    { pre: 'Buying from different ', strong: 'dye lots', rest: ' — always buy enough boxes in one trip, from the same pallet if possible.' },
  ],
  relatedH2: 'Related calculators',
  reviewer: 'Reviewed by the RenoSheets team for calculation accuracy. Method: total area with adjustable waste divided by 20 sq ft per box. Last updated 2026-05-24. Estimates only — verify quantities before purchasing.',
  faqs: [
    { q: 'How many boxes of laminate flooring do I need for 200 square feet?', a: 'With 12% waste and 20 sq ft per box, you need 224 sq ft total, which rounds up to 12 boxes. Always add at least 10% waste to account for cuts, transitions and future repairs.' },
    { q: 'How much waste should I add for laminate or vinyl plank flooring?', a: 'Add 10% for simple rectangular rooms. Use 12–15% for rooms with many angles, bay windows or doorways. If installing on a diagonal (45 degrees), add 15–20% because diagonal cuts waste the ends of every row.' },
    { q: 'Do I need underlayment under laminate flooring?', a: 'Yes, unless the planks have pre-attached underlayment (check the box). Underlayment provides cushioning, reduces sound transmission, and acts as a moisture barrier over concrete. One roll covers about 200 sq ft.' },
    { q: 'What is the expansion gap for floating floors?', a: 'Leave a 3/8 in (10 mm) gap around all fixed objects — walls, cabinets, pipes, door frames. This allows the floor to expand and contract with temperature changes. Plastic spacers hold the gap while you install. Cover it with baseboard or quarter-round.' },
    { q: 'How many planks are in a box of laminate or vinyl plank?', a: 'It varies by brand and width, but a typical box of 5-inch-wide planks covers about 20 sq ft. Check the box label for exact coverage before buying. Most big-box stores list sq ft per box on the price tag.' },
    { q: 'What is a transition strip and do I need one?', a: 'A transition strip covers the gap between two different floor surfaces — laminate to tile, hardwood to carpet, or two different floating floors meeting in a doorway. You need one strip per doorway where the flooring type changes or where two independently-floating rooms meet.' },
  ],
}

const FR: CalcContent = {
  title: 'Calculateur de plancher — Combien de boîtes me faut-il? | RenoSheets',
  desc: 'Calculateur de plancher gratuit pour le stratifié, le vinyle et le bois franc. Entrez les dimensions des pièces pour obtenir le nombre de boîtes, plus la sous-couche, les cales et les baguettes de transition. Sans inscription.',
  h1: 'Calculateur de plancher',
  intro: 'Entrez les dimensions de vos pièces pour obtenir le nombre de boîtes de stratifié, de vinyle ou de bois franc — plus la sous-couche, les cales et les transitions.',
  flooringTypeLabel: 'Type de revêtement',
  typeLaminate: 'Stratifié',
  typeVinyl: 'Vinyle',
  typeHardwood: 'Bois franc',
  boardWidthLabel: 'Largeur de planche',
  boardWidthTip: 'Largeurs courantes : 3 po (bois franc à lames étroites), 5 po (bois franc/stratifié standard), 7 po (lame large). Consultez la fiche du produit pour la couverture exacte.',
  inSuffix: 'po',
  transitionLabel: 'Baguettes de transition requises',
  transitionTip: 'Nombre de seuils de porte ou de transitions de plancher. 1 baguette par ouverture où le plancher rencontre une surface différente.',
  transitionSuffix: 'baguettes',
  glueDown: 'Pose collée (ajoute de l’adhésif)',
  glueDownTip: 'Cochez si votre vinyle ou votre bois d’ingénierie est posé avec de l’adhésif plutôt qu’en plancher flottant. La plupart des vinyles et stratifiés sont flottants.',
  wasteTip: '10 % pour une pose droite; 12 % pour la plupart des pièces (par défaut); 15–20 % pour une pose en diagonale ou des pièces avec beaucoup d’angles.',
  primaryName: 'Boîtes',
  primaryLabel: 'de revêtement (20 pi² / boîte)',
  secondaryName: 'Planches',
  secondaryLabel: (widthIn) => `planches de ${widthIn} po de large`,
  savePrefix: 'Plancher',
  saveSummary: (boxes, type, rolls) => `${boxes} boîtes (${type}) · ${rolls} rouleaux de sous-couche`,
  areaSummary: (total, withWaste, boxes) => `${total} pi² net → ${withWaste} pi² avec perte → ${boxes} boîtes (20 pi²/boîte)`,
  assumptions: (total, rooms, wastePct) => `${total} pi² sur ${rooms} pièce${rooms === 1 ? '' : 's'}, ${wastePct} % de perte, 20 pi² par boîte.`,
  aboutH2: 'Ce qui influence vraiment la quantité de plancher nécessaire',
  aboutP1: 'Le pourcentage de perte devrait suivre votre motif de pose, pas seulement la forme de la pièce. Une pose droite et parallèle sur la longueur d’une pièce rectangulaire est la plus efficace — la recommandation habituelle de 10 % des fabricants suppose exactement ce cas. Une pose en diagonale, un motif chevron, ou une pièce pleine de recoins, de garde-robes et de transitions de porte pousse la perte réelle plus près de 15–20 %, car plus de coupes deviennent trop courtes pour être réutilisées. La largeur des planches compte pour une autre raison : des planches plus étroites en demandent davantage et créent plus de joints, tandis qu’un plancher à larges planches couvre la surface plus vite mais montre les écarts plus visiblement si le sous-plancher n’est pas plat. Achetez toujours du même lot de teinture et gardez la boîte supplémentaire — les fabricants ne garantissent pas la correspondance des couleurs entre les lots de production, alors une réparation future avec un lot différent peut se voir.',
  howH2: 'Comment calculer les boîtes de plancher',
  howP1: 'Le revêtement se vend à la boîte (habituellement 20 pi² pour le stratifié et le vinyle). La formule est simple :',
  formula: `surface totale (pi²) = somme des longueurs × largeurs des pièces
surface avec perte = surface totale × (1 + % perte)
boîtes = arrondi.sup(surface avec perte ÷ 20)`,
  howP2: 'Pour le bois franc posé à la planche, divisez la surface par la largeur de planche multipliée par la longueur moyenne de planche. Achetez toujours une boîte de plus du même lot de teinture pour les réparations — la couleur varie d’un lot à l’autre.',
  mistakesH3: 'Erreurs fréquentes',
  mistakes: [
    { pre: 'Ne pas ', strong: 'laisser de jeux de dilatation', rest: ' aux murs et aux objets fixes — le plancher gondolera par temps chaud.' },
    { pre: 'Sauter la ', strong: 'sous-couche', rest: ' sur le béton — la vapeur d’humidité endommagera les planches avec le temps.' },
    { pre: 'Poser sur un ', strong: 'sous-plancher inégal', rest: ' — les planchers flottants amplifient les bosses et les creux, causant des craquements et la séparation des joints à clic.' },
    { pre: 'Acheter dans différents ', strong: 'lots de teinture', rest: ' — achetez toujours assez de boîtes en un seul achat, de la même palette si possible.' },
  ],
  relatedH2: 'Calculateurs connexes',
  reviewer: 'Révisé par l’équipe RenoSheets pour l’exactitude des calculs. Méthode : surface totale avec marge de perte ajustable divisée par 20 pi² par boîte. Dernière mise à jour 2026-05-24. Estimations seulement — vérifiez les quantités avant d’acheter.',
  faqs: [
    { q: 'Combien de boîtes de plancher stratifié pour 200 pieds carrés?', a: 'Avec 12 % de perte et 20 pi² par boîte, il vous faut 224 pi² au total, ce qui arrondit à 12 boîtes. Ajoutez toujours au moins 10 % de perte pour tenir compte des coupes, des transitions et des réparations futures.' },
    { q: 'Combien de perte ajouter pour un plancher stratifié ou de vinyle?', a: 'Ajoutez 10 % pour les pièces rectangulaires simples. Utilisez 12–15 % pour les pièces avec beaucoup d’angles, des baies vitrées ou des seuils de porte. Pour une pose en diagonale (45 degrés), ajoutez 15–20 % car les coupes en diagonale gaspillent le bout de chaque rangée.' },
    { q: 'Ai-je besoin d’une sous-couche sous un plancher stratifié?', a: 'Oui, sauf si les planches ont une sous-couche déjà fixée (vérifiez la boîte). La sous-couche amortit, réduit la transmission du son et sert de pare-humidité sur le béton. Un rouleau couvre environ 200 pi².' },
    { q: 'Quel est le jeu de dilatation des planchers flottants?', a: 'Laissez un jeu de 3/8 po (10 mm) autour de tous les objets fixes — murs, armoires, tuyaux, cadres de porte. Cela permet au plancher de se dilater et de se contracter avec les variations de température. Des cales de plastique maintiennent le jeu pendant la pose. Couvrez-le avec une plinthe ou un quart-de-rond.' },
    { q: 'Combien de planches y a-t-il dans une boîte de stratifié ou de vinyle?', a: 'Cela varie selon la marque et la largeur, mais une boîte typique de planches de 5 po de large couvre environ 20 pi². Vérifiez l’étiquette de la boîte pour la couverture exacte avant d’acheter. La plupart des grandes surfaces indiquent les pi² par boîte sur l’étiquette de prix.' },
    { q: 'Qu’est-ce qu’une baguette de transition et m’en faut-il une?', a: 'Une baguette de transition couvre le joint entre deux surfaces de plancher différentes — stratifié à carrelage, bois franc à tapis, ou deux planchers flottants qui se rejoignent dans une porte. Il vous faut une baguette par seuil où le type de revêtement change ou où deux pièces flottant indépendamment se rejoignent.' },
  ],
}

export default function FlooringCalculator() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)
  const tr = (k: string) => t(locale, k)

  const FLOORING_TYPES: { v: FlooringType; l: string }[] = [
    { v: 'laminate', l: c.typeLaminate },
    { v: 'vinyl-plank', l: c.typeVinyl },
    { v: 'hardwood', l: c.typeHardwood },
  ]

  const [i, setI] = useState<FlooringInputs>(DEFAULT_FLOORING)
  const [saved, setSaved] = useState(false)
  const r = useMemo(() => computeFlooring(i, locale), [i, locale])
  const set = (patch: Partial<FlooringInputs>) => {
    setI((p) => ({ ...p, ...patch }))
    setSaved(false)
  }
  const setArea = (idx: number, patch: Partial<FloorArea>) =>
    set({ areas: i.areas.map((a, k) => (k === idx ? { ...a, ...patch } : a)) })
  const cvDim = (ft: number) => toUnit(ft, i.unit)
  const areaDefault = (idx: number) => (locale === 'fr' ? `Surface ${idx + 1}` : `Area ${idx + 1}`)
  const applyPreset = (idx: number, label: string) => {
    if (label === CUSTOM_ROOM_KEY) {
      // Switching to "Custom room" — give the area a default name unless the
      // user already typed a custom one. Empty current (the "Select a room"
      // placeholder state) also gets the default so the input appears.
      const current = i.areas[idx].name
      if (!current || matchedPreset(current)) setArea(idx, { name: areaDefault(idx) })
      return
    }
    const p = ROOM_PRESETS.find((rp) => rp.label === label)
    if (!p) return
    setArea(idx, { name: presetLabel(p, locale), length: cvDim(p.lFt), width: cvDim(p.wFt) })
  }
  // New areas start with an empty name so the dropdown shows the "Select a
  // room" placeholder — user picks a preset or "Custom room" themselves.
  const addArea = () =>
    set({ areas: [...i.areas, { name: '', length: cvDim(CUSTOM_DIMS.lFt), width: cvDim(CUSTOM_DIMS.wFt) }] })
  const removeArea = (idx: number) => set({ areas: i.areas.filter((_, k) => k !== idx) })
  const lin = i.unit === 'imperial' ? (locale === 'fr' ? 'pi' : 'ft') : 'm'

  const onSave = async () => {
    const sqLabel = locale === 'fr' ? 'pi²' : 'sq ft'
    await saveSheet({
      id: genId(),
      type: 'flooring',
      name: `${c.savePrefix} — ${r.totalArea} ${sqLabel}`,
      createdAt: new Date().toISOString(),
      summary: c.saveSummary(r.boxes, i.flooringType, r.underlaymentRolls),
      lines: r.lines.filter((l) => l.kind === 'consumable').map((l) => ({ label: l.label, qty: l.qty, unit: l.unit })),
    })
    setSaved(true)
  }

  const related = calcsInCategory(CATEGORY).filter((x) => x.slug !== 'flooring')

  // Step-by-step "How we got this" content for the ⓘ button on the result card.
  // Mirrors computeFlooring: sum areas, × (1 + waste%), ÷ 20 sq ft per box.
  const sqftWord = locale === 'fr' ? 'pi²' : 'sq ft'
  const buildExplain = (): React.ReactNode => (
    <ol className="list-decimal space-y-1 pl-5">
      <li>
        {i.inputMode === 'area'
          ? (locale === 'fr' ? 'Surface saisie' : 'Area you entered')
          : (locale === 'fr'
              ? `Surface totale (${i.areas.length} ${i.areas.length === 1 ? 'pièce' : 'pièces'})`
              : `Total area (${i.areas.length} ${i.areas.length === 1 ? 'room' : 'rooms'})`)}:
        {' '}<strong>{r.totalArea} {sqftWord}</strong>
      </li>
      <li>{locale === 'fr' ? `+ ${i.wastePct} % de perte` : `+ ${i.wastePct}% waste`}: <strong>{r.areaWithWaste} {sqftWord}</strong></li>
      <li>{locale === 'fr' ? '÷ 20 pi²/boîte' : '÷ 20 sq ft/box'} = <strong>{(r.areaWithWaste / 20).toFixed(2)}</strong></li>
      <li>{locale === 'fr' ? 'Arrondi à la boîte entière' : 'Rounded up to whole boxes'} = <strong>{r.boxes} {locale === 'fr' ? (r.boxes === 1 ? 'boîte' : 'boîtes') : (r.boxes === 1 ? 'box' : 'boxes')}</strong></li>
      <li>
        {locale === 'fr'
          ? `Planches: ${r.areaWithWaste} pi² ÷ (${i.boardWidthIn}/12 × 4 pi/planche)`
          : `Planks: ${r.areaWithWaste} sq ft ÷ (${i.boardWidthIn}/12 × 4 ft/plank)`}
        = <strong>{r.planks} {locale === 'fr' ? (r.planks === 1 ? 'planche' : 'planches') : (r.planks === 1 ? 'plank' : 'planks')}</strong>
      </li>
    </ol>
  )

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <Seo title={c.title} description={c.desc} path={PATH} schema={softwareAppSchema(calcName('flooring', 'Flooring Calculator', locale), c.desc, localizedHref(PATH, locale))} />
      <Breadcrumbs
        items={[
          { name: tr('crumb.home'), href: L('/') },
          { name: categoryName(CATEGORY, 'Flooring & Tile', locale), href: L(`/calculators/${CATEGORY}/`) },
          { name: calcName('flooring', 'Flooring Calculator', locale), href: L(PATH) },
        ]}
      />

      <h1 className="text-3xl">{c.h1}</h1>
      <p className="mt-1 max-w-content text-text-muted">{c.intro}</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <form className="rounded-xl border border-border bg-surface p-5 lg:col-span-3" onSubmit={(e) => e.preventDefault()}>
          <div className="mb-5 flex flex-wrap gap-6">
            <Segmented
              label={tr('calc.input')}
              value={i.inputMode}
              onChange={(v) => {
                if (v === 'area' && i.inputMode === 'dimensions') {
                  const sum = i.areas.reduce((s, a) => s + Math.max(0, a.length) * Math.max(0, a.width), 0)
                  set({ inputMode: v, totalArea: Math.round(sum * 10) / 10 })
                } else {
                  set({ inputMode: v })
                }
              }}
              options={[{ v: 'dimensions', l: tr('calc.dimensions') }, { v: 'area', l: tr('calc.totalArea') }]}
            />
          </div>

          {i.inputMode === 'dimensions' ? (
            <div className="space-y-3">
              {i.areas.map((a, idx) => {
                const isCustom = dropdownValue(a.name) === CUSTOM_ROOM_KEY
                return (
                <div key={idx} className="rounded-md border border-border p-3">
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <select
                      value={dropdownValue(a.name)}
                      onChange={(e) => applyPreset(idx, e.target.value)}
                      aria-label={tr('calc.roomType')}
                      className={`rounded-md border border-border bg-surface-2 px-2 py-1.5 text-sm text-text-body focus:border-brand focus:outline-none ${isCustom ? '' : 'w-full sm:w-1/2'}`}
                    >
                      <option value="" disabled>{tr('calc.selectRoom')}</option>
                      {ROOM_PRESETS.map((p) => (
                        <option key={p.label} value={p.label}>{presetLabel(p, locale)}</option>
                      ))}
                      <option value={CUSTOM_ROOM_KEY}>{tr('calc.customRoom')}</option>
                    </select>
                    {isCustom && (
                      <input
                        type="text"
                        value={a.name ?? ''}
                        onChange={(e) => setArea(idx, { name: e.target.value })}
                        placeholder={tr('calc.roomName')}
                        className="min-w-0 flex-1 rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text outline-none focus:border-brand"
                      />
                    )}
                    {i.areas.length > 1 && (
                      <button type="button" onClick={() => removeArea(idx)} className="text-xs text-danger hover:underline whitespace-nowrap">{tr('calc.remove')}</button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <SmartField label={tr('calc.length')} imperialValue={a.length} onChangeImperial={(n) => setArea(idx, { length: n })} imperialUnit="ft" />
                    <SmartField label={tr('calc.width')} imperialValue={a.width} onChangeImperial={(n) => setArea(idx, { width: n })} imperialUnit="ft" />
                  </div>
                </div>
                )
              })}
              <button type="button" onClick={addArea} className="w-full rounded-md border border-dashed border-border py-2 text-sm font-medium text-brand-text hover:bg-surface-2">
                {tr('calc.addRoom')}
              </button>
            </div>
          ) : (
            <div className="rounded-md border border-border p-3">
              <SmartField
                label={tr('calc.totalArea')}
                imperialValue={i.totalArea}
                onChangeImperial={(n) => set({ totalArea: n })}
                imperialUnit="sq ft"
              />
              <p className="mt-2 text-xs text-text-subtle">{tr('calc.areaHint')}</p>
            </div>
          )}

          {/* Flooring type */}
          <div className="mt-4">
            <Segmented
              label={c.flooringTypeLabel}
              value={i.flooringType}
              onChange={(v) => set({ flooringType: v as FlooringType })}
              options={FLOORING_TYPES}
            />
          </div>

          {/* Board width */}
          <div className="mt-4">
            <span className="mb-1 flex items-center text-sm font-medium text-text-body">
              {c.boardWidthLabel}
              <InfoTip text={c.boardWidthTip} />
            </span>
            <SmartField label="" imperialValue={i.boardWidthIn} onChangeImperial={(n) => set({ boardWidthIn: n })} imperialUnit="in" />
          </div>

          {/* Transition strips */}
          <div className="mt-4">
            <span className="mb-1 flex items-center text-sm font-medium text-text-body">
              {c.transitionLabel}
              <InfoTip text={c.transitionTip} />
            </span>
            <Field label="" value={i.transitionStrips} onChange={(n) => set({ transitionStrips: Math.round(n) })} suffix={c.transitionSuffix} />
          </div>

          {/* Glue-down option */}
          <label className="mt-4 flex items-center gap-2 text-sm text-text-body">
            <input type="checkbox" checked={i.needAdhesive} onChange={(e) => set({ needAdhesive: e.target.checked })} />
            {c.glueDown}
            <InfoTip text={c.glueDownTip} />
          </label>

          {/* Waste */}
          <div className="mt-5 rounded-lg bg-surface-2 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center font-medium text-text-body">
                {tr('calc.wasteAllowance')}
                <InfoTip text={c.wasteTip} />
              </span>
              <span className="tnum text-brand-text">{i.wastePct}%</span>
            </div>
            <input
              type="range" min={10} max={20} step={1} value={i.wastePct}
              onChange={(e) => set({ wastePct: parseInt(e.target.value) })}
              className="mt-1 w-full accent-brand"
            />
            <p className="mt-2 text-xs text-text-subtle">
              {c.areaSummary(r.totalArea, r.areaWithWaste, r.boxes)}
            </p>
          </div>
          <CalcActions onSave={onSave} saved={saved} locale={locale} className="mt-6 border-t border-border pt-4" />
        </form>

        <div className="lg:col-span-2">
          <ResultSheet
            primary={{ name: c.primaryName, label: c.primaryLabel, qty: r.boxes, unit: 'box', query: `${i.flooringType} flooring planks` }}
            secondary={
              r.planks > 0
                ? { name: c.secondaryName, label: c.secondaryLabel(i.boardWidthIn), qty: r.planks, unit: 'plank', query: `${i.flooringType} floor planks` }
                : null
            }
            lines={r.lines}
            assumptions={c.assumptions(r.totalArea, i.areas.length, i.wastePct)}
            explain={buildExplain()}
          />
        </div>
      </div>

      <article className="mx-auto mt-8 max-w-content text-text-body">
        <h2 className="text-2xl">{c.aboutH2}</h2>
        <p className="mt-3">{c.aboutP1}</p>
        <h2 className="mt-8 text-2xl">{c.howH2}</h2>
        <p className="mt-3">{c.howP1}</p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-surface-2 p-3 text-sm text-text">{c.formula}</pre>
        <p className="mt-3">{c.howP2}</p>
        <h3 className="mt-6 text-xl">{c.mistakesH3}</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {c.mistakes.map((m, k) => <li key={k}>{m.pre}<strong>{m.strong}</strong>{m.rest}</li>)}
        </ul>
      </article>

      <Faq items={c.faqs} />

      <section className="mt-10">
        <h2 className="text-2xl">{c.relatedH2}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {related.map((x) => (
            <a
              key={x.slug}
              href={L(`/calculators/${x.slug}/`)}
              className={`rounded-lg border p-3 text-sm transition-colors ${
                x.live
                  ? 'border-border bg-surface-2 font-medium text-brand-text hover:border-brand hover:bg-surface'
                  : 'border-dashed border-border bg-surface-2 text-text-muted'
              }`}
            >
              {calcName(x.slug, x.name, locale)} {x.live ? '' : <span className="text-text-subtle">{tr('calc.soon')}</span>}
            </a>
          ))}
        </div>
      </section>

      <div className="mx-auto mt-8 max-w-content border-t border-border pt-4 text-xs text-text-subtle">{c.reviewer}</div>
    </div>
  )
}
